import { Typography, Box, Alert, AlertTitle, Button } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, UserTypeSelectField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import RareButton from '../buttons/RareButton';
import { USER_TYPES } from '../../../services/userRoles';
import StatusChip from '../StatusChip';
import { requestPasswordReset } from '../../../services/passwordReset';

interface UserDetailProps {
    user_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
    onStatusChanged?: () => void;
}

const UserDetailCard: React.FC<UserDetailProps> = ({ user_id, handleRefreshButton, handleBackButton, onStatusChanged }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const [user, setUser] = useState<any>(null);
    const [activeStatus, setActiveStatus] = useState<boolean | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);
    const [creationTime] = useState(() => dayjs());
    const create = !user_id


    useEffect(() => {
        if (user_id) {
            const fetchUser = async () => {
                try {
                    const url = `users/${user_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });

                    const fetchedUser = response.data;
                    setUser(fetchedUser);
                    setActiveStatus(Boolean(fetchedUser.is_active));

                } catch (error) {
                    console.error('Error fetching user');
                    setErrors(`No User found with ID ${user_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchUser();
        }
    }, [user_id]);

    const validationSchema = () => {
        const name = Yup.string().trim().max(150, 'Use at most 150 characters.');
        return Yup.object().shape({
            first_name: create ? name.required('First name is required.') : name,
            last_name: create ? name.required('Last name is required.') : name,
            email: Yup.string().trim().email('Enter a valid email address.').max(254, 'Use at most 254 characters.').required("Email is required."),
            user_type: Yup.string().test('portal-role', 'Choose a supported portal role.', value =>
                value === USER_TYPES.portalUser || value === USER_TYPES.customerAccountManager || (!create && !!value && value === user?.user_type)),
        });
    }


    const formik = useFormik({
        enableReinitialize: !create,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues:
            (!create && user) ? {
                id: user.id || '',
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                user_type: user.user_type || '',
                date_joined: user.date_joined ? dayjs(user.date_joined).format('YYYY-MM-DD HH:mm:ss') : "", // Cant edit - Set to null if no date
                last_login: user.last_login ? dayjs(user.last_login).format('YYYY-MM-DD HH:mm:ss') : "Not yet logged in", // Cant edit - Set to null if no date
            }
                : {
                    id: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    user_type: USER_TYPES.portalUser,
                    date_joined: creationTime,
                    last_login: creationTime,
                },
        validationSchema,
        onSubmit: async (values) => {
            if (!create && !user) return;
            setErrors(null);
            const data: Record<string, unknown> = {
                first_name: values.first_name.trim(),
                last_name: values.last_name.trim(),
                email: values.email.trim(),
            }

            if (create) {
                data.user_type = values.user_type || USER_TYPES.portalUser;
                data.is_active = true;
            } else if (values.user_type && values.user_type !== user?.user_type) {
                data.user_type = values.user_type;
            }

            const emailChanged = user?.email !== values.email.trim();

            const url = user ? `users/${user.id}/` : `users/`
            const method = user ? 'patch' : 'post'

            let saved = false;
            try {
                // 1) Create/update user
                const response = await api({
                    method,
                    url,
                    data,
                    headers: { Authorization: `Bearer ${authData.access}` },
                });
                saved = true;
                // A failed follow-up email must not leave the saved address stale
                // for a later reactivation or another edit in this still-open card.
                if (!create) setUser({ ...user, ...data, ...response.data });
                // The server sends the initial invitation as part of user creation.
                // Existing users receive a fresh setup link after an email change.
                if (!create && emailChanged) {
                    await requestPasswordReset(values.email);
                }

                handleRefreshButton();
            } catch (err: any) {
                console.error('Error saving user');

                if (saved) {
                    setErrors('User changes were saved, but the password setup email could not be sent. Ask the user to use Forgot Password.');
                    return;
                }

                const resp = err.response;
                if (resp?.status === 400) {
                    // DRF returns arrays per field
                    const field = Object.keys(resp.data)[0];
                    const msgs = Array.isArray(resp.data[field])
                        ? resp.data[field].join(', ')
                        : resp.data[field];
                    setErrors(`${field}: ${msgs}`);
                }
                else if (resp?.status === 403) {
                    setErrors(resp.data.detail || 'You do not have permission.');
                }
                else if (create && resp?.status === 503) {
                    setErrors('The user could not be created because the invitation email was not sent. Please try again.');
                }
                else {
                    setErrors(create ? 'The user could not be created. Please try again.' : 'The user could not be updated. Please try again.');
                }

            }
        },
    });

    // Formik applies fetched initial values in an effect. Wait for that effect
    // before allowing an immediate status change alongside unsaved edits.
    const formReady = create || (!!user && formik.values.id === user.id);

    const changeStatus = async () => {
        if (!user || !formReady || activeStatus === null || statusBusy) return;
        const nextStatus = !activeStatus;
        setStatusBusy(true);
        setErrors(null);
        try {
            const response = await api({
                method: 'patch',
                url: `users/${user.id}/`,
                data: { is_active: nextStatus },
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            if (response.data?.is_active !== nextStatus) throw new Error('Unexpected user status response');
            setActiveStatus(nextStatus);
            onStatusChanged?.();
            if (nextStatus) {
                try {
                    await requestPasswordReset(user.email);
                } catch {
                    setErrors('The account was reactivated, but the password setup email could not be sent. Ask the user to use Forgot Password.');
                }
            }
        } catch (failure: any) {
            const detail = failure.response?.data?.is_active;
            setErrors(Array.isArray(detail) ? detail.join(' ') :
                typeof detail === 'string' ? detail :
                    failure.response?.status === 403 ? 'You do not have permission to change this account status.' :
                        'The account status could not be changed. Please try again.');
        } finally {
            setStatusBusy(false);
        }
    };

    useEffect(() => {
        if (errors) errorRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [errors]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{}}>
                <Box data-testid="Box-3lo3" sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {errors && (
                        <Alert ref={errorRef} data-testid="Alert-knqx" severity="error">
                            <AlertTitle data-testid="AlertTitle-8fw4">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    <Box data-testid="div-vurq" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <Field data-testid="Field-w9xk" name="first_name" type="text" className="form-control" label="First Name" component={TextEditField} />
                            <Field data-testid="Field-0046" name="last_name" type="text" className="form-control" label="Last Name" component={TextEditField} />
                        </Box>
                        <Field data-testid="Field-vxyg" name="email" type="text" className="form-control" label="Email" component={TextEditField} />
                        {(authData.isPortalUser || authData.isSuperUser) && (
                            <Field data-testid="Field-499o" name="user_type" type="text" className="form-control" label="User Type" component={UserTypeSelectField} />
                        )}
                    </Box>
                    {!create && user && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'max-content minmax(0, 1.15fr) minmax(0, 1fr) auto' }, gap: 2, color: '#58646a', borderTop: '1px solid #e1e8e6', pt: 2 }}>
                                {[
                                    ['User ID', formik.values.id],
                                    ['Date joined', formik.values.date_joined],
                                    ['Last login', formik.values.last_login],
                                ].map(([label, value]) => (
                                    <Box key={label} sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" display="block">{label}</Typography>
                                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', color: colorConfigs.brand.logoText }}>
                                            {dayjs.isDayjs(value) ? value.format('YYYY-MM-DD HH:mm:ss') : value}
                                        </Typography>
                                    </Box>
                                ))}
                                <Box sx={{ justifySelf: 'end', alignSelf: 'end' }}>
                                    <StatusChip enabled={activeStatus ?? false} trueLabel="Active" falseLabel="Inactive" />
                                </Box>
                            </Box>
                    )}
                    <Box data-testid="div-zpb9" className="form-group" sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #e1e8e6', pt: 1.5 }}>
                        {!create && user && (
                            <Button
                                type="button"
                                disabled={!formReady || statusBusy || formik.isSubmitting || activeStatus === null}
                                onClick={() => void changeStatus()}
                                sx={{
                                    mr: 'auto', textTransform: 'none', fontWeight: 700,
                                    color: activeStatus ? '#b3261e' : colorConfigs.buttons.active,
                                    backgroundColor: activeStatus ? '#fcf4f3' : '#f1f8f2',
                                    '&:hover': { backgroundColor: activeStatus ? '#fbe9e7' : '#e5f3e8' },
                                }}
                            >
                                {statusBusy ? 'Updating status…' : activeStatus ? 'Deactivate account' : 'Reactivate account'}
                            </Button>
                        )}
                        <RareButton data-testid="RareButton-9jec"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Cancel
                        </RareButton>
                        <RareButton data-testid="RareButton-hhee"
                            type="submit"
                            variant="success"
                            disabled={(!create && (!formik.dirty || !user)) || statusBusy || formik.isSubmitting}
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            {formik.isSubmitting ? (create ? 'Creating…' : 'Saving…') : create ? 'Create User' : 'Save Changes'}
                        </RareButton>
                    </Box>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default UserDetailCard;
